import React, { useState } from "react";
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { ArrowRight, Info, X } from "lucide-react";
import LandingSection from "../components/LandingSection";
import Footer from "../components/Footer";
import ActionButton from "../utils/ActionButton";

const data = [
  { county: "BEAVER", estimate: 2777, actual: 2541, percentChange: 9.3 },
  { county: "BOX ELDER", estimate: 24383, actual: 23751, percentChange: 2.7 },
  { county: "CACHE", estimate: 64800, actual: 64644, percentChange: 0.2 },
  { county: "CARBON", estimate: 9200, actual: 8985, percentChange: 2.4 },
  { county: "DAGGETT", estimate: 545, actual: 557, percentChange: -2.1 },
  { county: "DAVIS", estimate: 145489, actual: 144480, percentChange: 0.7 },
  { county: "DUCHESNE", estimate: 9227, actual: 8864, percentChange: 4.1 },
  { county: "EMERY", estimate: 3542, actual: 3702, percentChange: -4.3 },
  { county: "GARFIELD", estimate: 3047, actual: 2910, percentChange: 4.7 },
  { county: "GRAND", estimate: 7461, actual: 7514, percentChange: -0.7 },
  { county: "IRON", estimate: 26044, actual: 24561, percentChange: 6 },
  { county: "JUAB", estimate: 4464, actual: 4388, percentChange: 1.7 },
  { county: "KANE", estimate: 4611, actual: 4433, percentChange: 4 },
  { county: "MILLARD", estimate: 4986, actual: 5001, percentChange: -0.3 },
  { county: "MORGAN", estimate: 3483, actual: 3213, percentChange: 8.4 },
  { county: "PIUTE", estimate: 281, actual: 297, percentChange: -5.4 },
  { county: "RICH", estimate: 1508, actual: 1611, percentChange: -6.4 },
  { county: "SALT LAKE", estimate: 813686, actual: 803011, percentChange: 1.3 },
  { county: "SAN JUAN", estimate: 4928, actual: 4772, percentChange: 3.3 },
  { county: "SANPETE", estimate: 9573, actual: 9417, percentChange: 1.7 },
  { county: "SEVIER", estimate: 9978, actual: 9834, percentChange: 1.5 },
  { county: "SUMMIT", estimate: 31821, actual: 30983, percentChange: 2.7 },
  { county: "TOOELE", estimate: 20493, actual: 20400, percentChange: 0.5 },
  { county: "UINTAH", estimate: 16149, actual: 15246, percentChange: 5.9 },
  { county: "UTAH", estimate: 305035, actual: 300355, percentChange: 1.6 },
  { county: "WASATCH", estimate: 12794, actual: 12268, percentChange: 4.3 },
  { county: "WASHINGTON", estimate: 88728, actual: 83570, percentChange: 6.2 },
  { county: "WAYNE", estimate: 1421, actual: 1320, percentChange: 7.7 },
  { county: "WEBER", estimate: 124494, actual: 123367, percentChange: 0.9 },
];

const UtahJobs = () => {
  const [showModal, setShowModal] = useState(false);

  // Calculate dynamic values from data
  const totalEmployment = data.reduce(
    (sum, county) => sum + county.estimate,
    0
  );
  const averageGrowth = (
    data.reduce((sum, county) => sum + county.percentChange, 0) / data.length
  ).toFixed(1);

  // Find top performer
  const topPerformer = data.reduce(
    (max, county) => (county.percentChange > max.percentChange ? county : max),
    data[0]
  );

  // Get current date for display
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const previousYear = currentYear - 1;

  return (
    <div className="bg-gradient-to-b from-blue-50 to-white">
      <LandingSection
        data={{
          imageName: "jobs.webp",
          title: "Utah Employment Trends",
          visible: "",
        }}
      />

      <div className="verticals_section">
        <div className="verticals-intro">
          <div className="verticals-intro-sec1">
            <h2>Utah Employment Trends</h2>
          </div>
          <div className="verticals-intro-sec2">
            <p>
              Utah's employment trends reflect steady growth across key sectors
              like technology, healthcare, and construction, driven by a strong
              economy and expanding workforce.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-blue-900 mb-4">
            County Employment and Growth Rate
          </h1>
          <p className="text-xl text-blue-700">
            Utah Workforce Overview (
            {totalEmployment.toLocaleString("en-US", {
              notation: "compact",
              compactDisplay: "short",
              maximumFractionDigits: 2,
            })}{" "}
            jobs in July {currentYear})
          </p>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white  rounded-xl p-6 border border-blue-800 shadow-[0_8px_24px_rgba(37,99,235,0.6)] hover:shadow-[0_12px_32px_rgba(30,64,175,0.8)] transition-shadow duration-300">
            <h3 className="text-lg font-semibold text-blue-800 mb-2">
              Total Employment
            </h3>
            <p className="text-3xl font-bold text-blue-600">
              {totalEmployment.toLocaleString("en-US", {
                notation: "compact",
                compactDisplay: "short",
                maximumFractionDigits: 2,
              })}
            </p>
            <p className="text-sm text-gray-500">July {currentYear} estimate</p>
          </div>
          <div className="bg-white rounded-xl p-6 border border-blue-800 shadow-[0_8px_24px_rgba(37,99,235,0.6)] hover:shadow-[0_12px_32px_rgba(30,64,175,0.8)] transition-shadow duration-300">
            <h3 className="text-lg font-semibold text-blue-800 mb-2">
              Yearly Growth
            </h3>
            <p className="text-3xl font-bold text-green-600">
              +{averageGrowth}%
            </p>
            <p className="text-sm text-gray-500">Average across counties</p>
          </div>
          <div className="bg-white rounded-xl p-6 border border-blue-800 shadow-[0_8px_24px_rgba(37,99,235,0.6)] hover:shadow-[0_12px_32px_rgba(30,64,175,0.8)] transition-shadow duration-300">
            <h3 className="text-lg font-semibold text-blue-800 mb-2">
              Top Performer
            </h3>
            <p className="text-2xl font-bold text-blue-600">
              {topPerformer.county} County
            </p>
            <p className="text-sm text-gray-500">
              +{topPerformer.percentChange}% growth
            </p>
          </div>
        </div>

        {/* Chart Section */}
        <div className="bg-white rounded-xl p-6 border border-blue-800 shadow-[0_8px_24px_rgba(37,99,235,0.6)] hover:shadow-[0_12px_32px_rgba(30,64,175,0.8)] transition-shadow duration-300 mb-12">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 md:mb-0">
              Employment by County
            </h2>
            <button
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Info size={18} />
              Chart Details
              <ArrowRight size={18} />
            </button>
          </div>

          <div className="overflow-x-auto overflow-y-hidden">
            <div className="min-w-[800px]">
              <ResponsiveContainer width="100%" height={600}>
                <ComposedChart
                  data={data}
                  margin={{ top: 20, right: 20, left: 20, bottom: 80 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  {/* <XAxis
                    dataKey="county"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    tick={{ fontSize: 12 }}
                  /> */}
                  <XAxis
                    dataKey="county"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    tick={{ fontSize: 12 }}
                    label={{ value: "County", position: "bottom", offset: 50 }}
                  />

                  <YAxis
                    tickFormatter={(value) => value.toLocaleString("en-US")}
                    tick={{ fontSize: 8 }}
                    label={{
                      value: "Number of Employees",
                      angle: -90,
                      position: "insideLeft",
                      offset: 0,
                    }}
                  />

                  {/* <YAxis
                    tickFormatter={(value) => value.toLocaleString("en-US")}
                    tick={{ fontSize: 12 }}
                  /> */}
                  <Tooltip
                    formatter={(value, name) => [
                      value.toLocaleString("en-US"),
                      name === "estimate"
                        ? `July ${currentYear} Estimate`
                        : `July ${previousYear} Actual`,
                    ]}
                    labelFormatter={(label) => `County: ${label}`}
                  />
                  <Legend
                    wrapperStyle={{ paddingTop: "20px" }}
                    formatter={(value) =>
                      value === "estimate"
                        ? `July ${currentYear} Estimate`
                        : `July ${previousYear} Actual`
                    }
                  />
                  <Bar
                    dataKey="estimate"
                    fill="#014cb2"
                    name="estimate"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="actual"
                    fill="#cb0c0c"
                    name="actual"
                    radius={[4, 4, 0, 0]}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Data Summary */}
        <div className="bg-blue-50 rounded-xl p-6 mb-12 border border-blue-200">
          <h2 className="text-2xl font-bold text-blue-900 mb-2">
            Key Insights
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-blue-800 mb-2">
                Top Growing Counties
              </h3>
              <ul className="space-y-2">
                {data
                  .sort((a, b) => b.percentChange - a.percentChange)
                  .slice(0, 5)
                  .map((county) => (
                    <li key={county.county} className="flex justify-between">
                      <span>{county.county}</span>
                      <span className="font-medium text-green-600">
                        +{county.percentChange}%
                      </span>
                    </li>
                  ))}
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-blue-800 mb-2">
                Largest Employment Bases
              </h3>
              <ul className="space-y-2">
                {data
                  .sort((a, b) => b.estimate - a.estimate)
                  .slice(0, 5)
                  .map((county) => (
                    <li key={county.county} className="flex justify-between">
                      <span>{county.county}</span>
                      <span className="font-medium">
                        {county.estimate.toLocaleString("en-US")}
                      </span>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Source Footer */}
        <div className="text-center text-sm text-gray-500">
          <p>Data source: Utah Department of Workforce Services</p>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b p-4 sticky top-0 bg-white z-10">
              <h3 className="text-xl font-bold">Chart Details</h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700 flex items-center"
              >
                <X size={24} />
              </button>
            </div>
            <div className="p-6">
              <h4 className="font-bold text-lg mb-3">
                Employment and Job Growth Analysis
              </h4>
              <p className="mb-4">
                This visualization compares estimated employment for July{" "}
                {currentYear} with actual numbers from July {previousYear}{" "}
                across Utah counties.
              </p>

              <h5 className="font-semibold mb-2">Key Metrics:</h5>
              <ul className="list-disc pl-5 space-y-2 mb-4">
                <li>
                  <strong>July {currentYear} Estimate:</strong> Projected
                  employment numbers for each county
                </li>
                <li>
                  <strong>July {previousYear} Actual:</strong> Verified
                  employment figures from the previous year
                </li>
              </ul>

              <h5 className="font-semibold mb-2">How to Use This Chart:</h5>
              <ul className="list-disc pl-5 space-y-2">
                <li>Hover over bars to see exact numbers</li>
                <li>Compare county performance at a glance</li>
                <li>Identify growth trends across regions</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default UtahJobs;
